import { StarIcon } from "@heroicons/react/24/solid";
import { TagIcon } from "@heroicons/react/24/outline";
import { Tag } from "../../types/content";
import CardWithImageTopAndFooter from "./CardWithImageTopAndFooter";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useEffect, useState } from "react";
import { getReviewForContent, selectRating, submitReviewRating } from "../../slices/review";
import { addAlert } from "../../slices/alert";
import RatingModal from "./RatingModal";

export default function SupportCard({
  objectId,
  image,
  bgColour = "",
  href,
  name,
  synopsis,
  primaryTags,
  user,
  rating,
  reviews,
  price = "",
}: {
  objectId: string;
  image: string;
  bgColour?: string;
  href?: string;
  name: string;
  synopsis: string;
  primaryTags: Tag[];
  user: any;
  rating: number;
  reviews: number;
  price: string;
}) {
  const dispatch = useAppDispatch();

  // Scoped to this object. A single shared slot meant rating one card wrote its
  // score onto every other mounted card.
  const { score, rating: liveRating, reviews: liveReviews, loading } = useAppSelector(
    (state) => selectRating(state, objectId)
  );

  // Prefer the freshly fetched aggregate over the value baked into the list
  // payload -- that payload is served from a cache with a ~24h TTL, so right after
  // rating it still reports the old average.
  const displayRating = liveReviews > 0 ? liveRating : rating;
  const displayReviews = liveReviews > 0 ? liveReviews : reviews;

  useEffect(() => {
    if (loading) return;
    if (score === null) return;
    setStarRatingValue(score);
  }, [
    loading,
    score
  ]);

  const [showStarRatingModal, setShowStarRatingModal] = useState(false);
  const [starRatingText, setStarRatingText] = useState("");
  const [starRatingId, setStarRatingId] = useState("");
  const [starRatingValue, setStarRatingValue] = useState(0);

  async function triggerSubmitRating(e: any) {
    e.preventDefault();
    if (starRatingValue < 1) {
      dispatch(addAlert("Pick a star rating before submitting.", "orange"));
      return;
    }
    const res: any = await dispatch(submitReviewRating({
      objectId: starRatingId,
      objectType: "Support",
      score: starRatingValue
    }))
    if (res.payload?.status === 200) {
      setStarRatingValue(0);
      setStarRatingText("");
      setStarRatingId("");
      setShowStarRatingModal(false);
      dispatch(addAlert("Rating submitted, thanks!", "mint-green"));
    } else {
      // The thunk rejects with the server's reason string; a silent no-op here
      // looked identical to a rating that saved but did not refresh.
      const reason = typeof res.payload === "string" ? res.payload : "";
      dispatch(addAlert(reason || "Could not submit your rating. Please try again.", "orange"));
    }
  }

  return (
    <>
      <RatingModal
        open={showStarRatingModal}
        onClose={setShowStarRatingModal}
        title={starRatingText}
        value={starRatingValue}
        onChange={setStarRatingValue}
        onSubmit={triggerSubmitRating}
      />

      <CardWithImageTopAndFooter
        objectId={objectId}
        objectType="Support"
        image={image}
        bgColour={bgColour}
        href={href}
        dataTitle={name}
        dataAuthor={name}
        dataTags={primaryTags.map((tag: any) => tag.name).join(", ")}
        middleContent={
          <>
            {/* Name */}
            <h1
              className="text-sm text-dark-blue font-bold line-clamp-2 overflow-hidden text-ellipsis break-words"
              title={name} // This sets the full text as the tooltip on hover
            >
              {name}
            </h1>

            {/* Tags */}
            <div className="flex flex-wrap gap-[0.1rem]">
              {primaryTags.map((tag: Tag, index: number) => {
                return (
                  <span key={`tag-${index}`} className="flex items-center bg-transparent border border-dark-blue text-dark-blue px-1.5 py-0.2 rounded-full text-xxs font-bold">{tag.name}</span>
                );
              })}
            </div>

            {/* Synopsis */}
            <p title={synopsis} className="text-xs text-dark-blue line-clamp-2 overflow-hidden text-ellipsis break-words">{synopsis}</p>

          </>
        }
        footerContent={
          <>
            {/* Consumption */}
            <span className={`flex items-center text-white text-sm font-bold px-4 py-1 bg-yellow rounded-full`}>Support</span>

            {/* Rating */}
            <div
              className={`group flex items-center gap-1 cursor-pointer`}
              onClick={() => {
                if (user !== null) {
                  setStarRatingText(name);
                  setStarRatingId(objectId);
                  setStarRatingValue(0);
                  setShowStarRatingModal(true);
                  dispatch(getReviewForContent({
                    contentId: objectId,
                    contentType: "Support"
                  }));
                }
              }}
            >
              <StarIcon className={`shrink-0 h-5 w-5 text-${user !== null ? "yellow" : "secondary"}`} />
              {user !== null ? (
                <>
                  <span className="text-sm font-bold text-dark-blue">{displayRating}</span>
                  <span className="text-sm font-bold text-dark-blue">({displayReviews})</span>
                </>
              ) : (
                <span
                  className="
                relative bottom-14 mt-1 -translate-x-1/2
                px-2 py-1 bg-black text-white text-xs rounded
                opacity-0 group-hover:opacity-100 pointer-events-none z-50
              "
                >
                  You must be logged in to see the ratings!
                </span>
              )}
            </div>

            {/* Price */}
            {price && (
              <div className="flex items-center gap-1">
                <TagIcon className="h-5 w-5 text-dark-blue" />
                <span className="text-sm font-bold text-dark-blue">{price}</span>
              </div>
            )}
          </>
        }
      />
    </>
  );
}
